import _ from 'underscore';

import { Filter } from '~commonInfra/utils/Filter';
import { ServerApiMapper } from '~commonInfra/utils/ServerModelHelper';
import { Set } from '~commonInfra/utils/Set';
import { LongRunningAction } from '~permissions/services/LongRunningAction';
import { ILongRunningAction } from '~permissions/services/types/ILongRunningAction';
import { UpdateConnectionsOAuthRequest } from '~src/connectivity/serverApi/requests/OAuth/UpdateConnectionsOAuthRequest';
import { ConnectionDetailInfo, isRemote } from '~src/datasource/dataConnection/models/ConnectionDetailInfo';
import { IDataConnectionInfo } from '~src/datasource/dataConnection/models/IDataConnectionInfo';
import { CheckConnectionRequest, ICheckConnectionParams, ICheckConnectionResult } from '~src/datasource/dataConnection/serverApi/CheckConnectionRequest';
import { GetDataConnectionsRequest } from '~src/datasource/dataConnection/serverApi/GetDataConnectionsRequest';
import { IUpdateConnectionsParams, UpdateConnectionsRequest } from '~src/datasource/dataConnection/serverApi/UpdateConnectionsRequest';
import { IUpdateConnectionUsingRemoteQueryAgentParams, UpdateConnectionUsingRemoteQueryAgentRequest } from '~src/datasource/dataConnection/serverApi/UpdateConnectionUsingRemoteQueryAgentRequest';
import { IDataSourceBase } from '~src/datasource/models/IDataSourceInfo';
import { GetDataConnectionIdsFromDatasourcesRequest } from '~src/datasource/ServerApi/requests/GetDataConnectionIdsFromDatasourcesRequest';
import { GetDatasourceActionsRequest } from '~src/datasource/ServerApi/requests/GetDatasourceActionsRequest';
import { DatasourceIdToConnectionIdsMap } from '~src/datasource/ServerApi/types/DatasourceIdToConnectionIdsMap';
import { IDatasourceActions } from '~src/datasource/ServerApi/types/IDatasourceActions';
import { IListFetcher } from '~src/httpClient/fetchers/IListFetcher';
import { WebClientListResourceFetcher } from '~src/httpClient/fetchers/resourceFetchers/WebClientListResourceFetcher';
import { FieldValuesQuery } from '~src/httpClient/resources/FieldValuesQuery';
import { IActionResource, IFieldValuesResource, IListResource } from '~src/httpClient/resources/Resource';
import { ResourceQuery } from '~src/httpClient/resources/ResourceQuery';
import { GetFieldValuesRequest } from '~src/httpClient/serverApi/requests/GetFieldValuesRequest';
import { IFieldValue } from '~src/httpClient/serverApi/types/IFieldValue';
import { SliceableHelper } from '~src/httpClient/utils/SliceableHelper';
import { FilterFieldNames } from '~src/spacesPlatform/models/IFilterClause';

import { Messages } from '../../../codegen/Localize';
import { IResult } from '../../../httpClient/serverApi/types/IResponse';
import { RequestHelper } from '../../../httpClient/utils/RequestHelper';
import WebHttpClient from '../../../httpClient/WebHttpClient';
import { WebHttpClientWithErrorToastInstance } from '../../../httpClient/WebHttpClientWithErrorToast';

export interface IEmbeddedPasswordUpdate {
  isEmbedded: boolean;
  newPassword?: string;
}

// Represents the intended updates to a data connection. All fields are optional.
export interface IConnectionUpdate {
  server?: string;
  port?: string;
  username?: string;
  passwordUpdate?: IEmbeddedPasswordUpdate;
  usingRemoteQueryAgent?: boolean;
}

class DataConnectionsResource implements IListResource<IDataConnectionInfo>, IFieldValuesResource, IActionResource<IDatasourceActions> {

  public getDisplayUsername(dataconnection: IDataConnectionInfo): string {
    const details: ConnectionDetailInfo = dataconnection.connectionDetails;
    let text = '';
    if (details && isRemote(details)) {
      text = details.databaseUsername;
    }
    return text;
  }

  public getDisplayAuthentication(dataconnection: IDataConnectionInfo): string {
    const details: ConnectionDetailInfo = dataconnection.connectionDetails;
    let text = '';
    if (details) {
      if (isRemote(details)) {
        if (details.hasEmbeddedPassword) {
          text = Messages.hasPasswordEmbedded();
        } else {
          text = Messages.hasPasswordNotEmbedded();
        }
      } else if (details.type === 'file') {
        text = Messages.none();
      }
    }
    return text;
  }

  private listFetcher: IListFetcher<IDataConnectionInfo>;
  private fieldValuesFetcher: IListFetcher<IFieldValue>;

  constructor() {
    this.listFetcher = new WebClientListResourceFetcher(GetDataConnectionsRequest, ServerApiMapper.dataConnections);
    this.fieldValuesFetcher = new WebClientListResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);
  }

  public all(): ResourceQuery<IDataConnectionInfo> {
    return new ResourceQuery(this.listFetcher);
  }

  public getConnectionIdsFromDatasourceIds(datasourceIds: string[]): Promise<DatasourceIdToConnectionIdsMap> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(datasourceIds, (idsBlock) =>
        new GetDataConnectionIdsFromDatasourcesRequest(idsBlock)
      ),
      (results: DatasourceIdToConnectionIdsMap[]) => {
        return Object.assign({}, ...results);
      });
  }

  public async getConnections(datasource: IDataSourceBase){
    const dataConnection = await this.getFromDatasource(datasource);
    return dataConnection;
 }


  public getFromDatasource(datasource: IDataSourceBase): Promise<IDataConnectionInfo[]> {
    return this.getFromDatasources(Set.singleton(datasource, ds => ds.id));
  }

  public getFromDatasourceByIds(datasourceIds: string[]): Promise<IDataConnectionInfo[]> {
    const makeQueryFromDatasourceIdsBlock = (idsBlock: string[]) =>
        this.all().addFilter(Filter.stringFieldInArray(FilterFieldNames.datasourceId, idsBlock));

    return SliceableHelper.fetchAllForSliceableWithArrayParam(datasourceIds, makeQueryFromDatasourceIdsBlock);
  }

  public getFromDatasources(datasources: Set<IDataSourceBase>): Promise<IDataConnectionInfo[]> {
    const datasourceIds = datasources.map(ds => ds.id);
    return this.getFromDatasourceByIds(datasourceIds);
  }

  public getForFlow(flowId: string): Promise<IDataConnectionInfo[]> {
    const query = this.all().addFilter(Filter.stringFieldEq(FilterFieldNames.flowId, flowId));
    return SliceableHelper.fetchAll(query).then(listResult => listResult.items);
  }

  public valuesForField(field: string): FieldValuesQuery {
    return new FieldValuesQuery(this.fieldValuesFetcher, field, 'dataConnection');
  }

  public actions(datasourceId: string): Promise<IDatasourceActions> {
    // Since actions/ownership are defined at a data source level, we get the available actions
    // for the connection's data source and the UI shows only those that are relevant to connections.
    // TODO: Figure out how to refactor this to get the action on the datasource ID instead of the connection ID.
    // It'll work for now, but will break when the unified datasources go away on the backend.
    return WebHttpClient.sendRequest(new GetDatasourceActionsRequest(datasourceId));
  }

  public checkExistingConnection(dataSourceId: string): Promise<ICheckConnectionResult> {
    const request = new CheckConnectionRequest({ id: dataSourceId });
    return WebHttpClientWithErrorToastInstance.sendRequest(request);
  }

  public checkConnection(dataSourceId: string, connectionUpdate: IConnectionUpdate): Promise<ICheckConnectionResult> {

    const requestParams: ICheckConnectionParams =
    {
      id: dataSourceId,
      server: connectionUpdate.server,
      port: connectionUpdate.port,
      username: connectionUpdate.username
    };

    DataConnections.setPasswordParams(requestParams, connectionUpdate.passwordUpdate);
    return WebHttpClientWithErrorToastInstance.sendRequest(new CheckConnectionRequest(requestParams));
  }

  public updateConnectionUsingRemoteQueryAgent(dataSourceIds: string[], usingRemoteQueryAgent: boolean): Promise<IResult> {

    const requestParams: IUpdateConnectionUsingRemoteQueryAgentParams =
      {
        ids: dataSourceIds,
        usingRemoteQueryAgent: usingRemoteQueryAgent
      };

    return WebHttpClientWithErrorToastInstance.sendRequest(new UpdateConnectionUsingRemoteQueryAgentRequest(requestParams));
  }

  public updateConnections(dataSourceIds: string[], connectionUpdate: IConnectionUpdate): Promise<IResult> {

    const requestParams: IUpdateConnectionsParams =
    {
      ids: dataSourceIds,
      server: connectionUpdate.server,
      port: connectionUpdate.port,
      username: connectionUpdate.username,
      usingRemoteQueryAgent: connectionUpdate.usingRemoteQueryAgent
    };

    DataConnections.setPasswordParams(requestParams, connectionUpdate.passwordUpdate);
    return WebHttpClientWithErrorToastInstance.sendRequest(new UpdateConnectionsRequest(requestParams))
      .then(LongRunningAction.getActionIdFromActionResult)
      .then((actionId: string) => LongRunningAction.trackAsyncJobProgressWithPromise(actionId))
      .then((longRunningAction: ILongRunningAction) => LongRunningAction.getActionResult<IResult>(longRunningAction));
  }

  public updateConnectionsOAuth(dataSourceIds: string[], keychainEntryId?: string, usingRemoteQueryAgent?: boolean): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance
      .sendRequest(new UpdateConnectionsOAuthRequest({
        ids: dataSourceIds,
        keychainEntryId: keychainEntryId,
        usingRemoteQueryAgent: usingRemoteQueryAgent,
      }))
      .then(LongRunningAction.getActionIdFromActionResult)
      .then((actionId: string) => LongRunningAction.trackAsyncJobProgressWithPromise(actionId))
      .then((longRunningAction: ILongRunningAction) => LongRunningAction.getActionResult<IResult>(longRunningAction));
  }

  private setPasswordParams(
      requestParams: { embedPassword?: boolean, password?: string },
      passwordUpdate: IEmbeddedPasswordUpdate): void {

    if (passwordUpdate) {
      requestParams.embedPassword = passwordUpdate.isEmbedded;

      if (_.isString(passwordUpdate.newPassword)) {
        // W-19373381: client-side password encryption was removed from this flow. We now send the
        // password in plaintext and rely on TLS to protect it in transit. The legacy RSA-1024 /
        // PKCS#1 v1.5 scheme it replaces was already below standard and capped plaintext at ~117
        // bytes, which broke long credentials such as Snowflake PATs. It also added little real
        // security: on HTTPS it is redundant with TLS, and on HTTP the server's public key was
        // fetched over the same unauthenticated channel, so an active MITM could substitute its own
        // key and decrypt — it only ever stopped passive sniffing, never an active attacker.
        // Removing it is a Security-accepted risk (see W-19373381 risk-register entry), scoped to
        // Tableau Server on-prem Edit-Connection. On HTTP installs a UI warning surfaces that
        // credentials are transmitted unprotected; HTTPS-by-default enforcement is tracked as a
        // separate follow-up owned by the core server team.
        requestParams.password = passwordUpdate.newPassword;
      }
    }
  }

}

export const DataConnections = new DataConnectionsResource();

